import { CompanyDTO } from "@shaarp/common";
import { createContext, useContext, useMemo, type ReactNode } from "react";

/**
 * Identity of the company whose page hosts the current chat. A mention of that
 * same company renders as plain text rather than a clickable preview chip: the
 * user is already on its page, so previewing it would just reopen what they are
 * looking at. Null in chats not scoped to a company (the main chat page), where
 * every mention stays clickable.
 */
export type CurrentCompanyMention = {
  id: string;
  name: string | null;
  domain: string | null;
  siren: string | null;
  siret: string | null;
};

const CurrentCompanyMentionContext =
  createContext<CurrentCompanyMention | null>(null);

export function CurrentCompanyMentionProvider({
  company,
  children,
}: {
  company: CompanyDTO | null;
  children: ReactNode;
}) {
  const value = useMemo<CurrentCompanyMention | null>(
    () =>
      company
        ? {
            id: company.id,
            name: company.name ?? null,
            domain: company.domain ?? null,
            siren: company.business?.siren ?? null,
            siret: company.business?.siret ?? null,
          }
        : null,
    [
      company?.id,
      company?.name,
      company?.domain,
      company?.business?.siren,
      company?.business?.siret,
    ],
  );

  return (
    <CurrentCompanyMentionContext.Provider value={value}>
      {children}
    </CurrentCompanyMentionContext.Provider>
  );
}

export function useCurrentCompanyMention(): CurrentCompanyMention | null {
  return useContext(CurrentCompanyMentionContext);
}
